import moment from 'moment-timezone';
import OrganizationApi from './OrganizationApi';
import { createSelector } from 'reselect';
import { receivedServerData } from '../../components/Common/Data/DataFetcher.js';
import { isPresent } from 'utils/isPresent';

export const ORGANIZATION_NAMESPACE = 'organization';
export const ORGANIZATION_INTERESTS_NAMESPACE = 'orgInterests';
export const NOTIFICATION_TYPE_JOB_SCHEDULED = 'job_scheduled';

export const getOrganization = createSelector(
  (state) => state.organization,
  (organization) => {
    return organization || {};
  }
);

export const getFeatures = createSelector(getOrganization, (organization) => {
  return organization.features || [];
});

export const getOrganizationUUID = createSelector(getOrganization, (organization) => organization.uuid);

export const getBusinessName = createSelector(getOrganization, (organization) => {
  return organization.company_name || '';
});

export const getEmail = createSelector(getOrganization, (organization) => {
  return organization.email || '';
});

export const getBusinessInitial = createSelector(getBusinessName, (businessName) => businessName.trim()[0] || '');

export const getBusinessPhone = createSelector(getOrganization, (organization) => {
  return organization.phone_number || '';
});

export const getBusinessWebsite = createSelector(getOrganization, (organization) => {
  return organization.company_website || '';
});

export const getOrganizationLicenseNumber = createSelector(getOrganization, (organization) => {
  return organization.contractor_license || '';
});

export const getOrganizationTermsAndConditionsUrl = createSelector(getOrganization, (organization) => {
  return organization.terms_url || '';
});

export const getSource = createSelector(getOrganization, (organization) => {
  return organization.source || '';
});

export const getProfile = createSelector(getOrganization, (organization) => {
  return organization.profile || '';
});

export const getDefaultIndustry = createSelector(getOrganization, (organization) => {
  return organization.default_industry || {};
});

export const getDefaultIndustryName = createSelector(getDefaultIndustry, (industry) => {
  return isPresent(industry.name) ? industry.name.toLowerCase() : '';
});

export const getDefaultIndustryId = createSelector(getDefaultIndustry, (industry) => {
  return industry.id || '';
});

export const getEmployeeCount = createSelector(getOrganization, (organization) => {
  return organization.declared_employee_count || '';
});

export const isXlOrg = createSelector(getEmployeeCount, (employeeCount) => {
  return employeeCount > 6;
});

export const getAddress = createSelector(getOrganization, (organization) => {
  const street_2 = isPresent(organization.street_line_2) ? organization.street_line_2 + ', ' : '';
  if (isPresent(organization.street)) {
    return `${organization.street}, ${street_2}${organization.city}, ${organization.state}, ${organization.zip}, ${organization.country}`;
  } else {
    return null;
  }
});

export const getStreet = createSelector(getOrganization, (organization) => {
  return organization.street || '';
});

export const getUnit = createSelector(getOrganization, (organization) => {
  return organization.street_line_2 || '';
});

export const getCity = createSelector(getOrganization, (organization) => {
  return organization.city || '';
});

export const getState = createSelector(getOrganization, (organization) => {
  return organization.state || '';
});

export const getZip = createSelector(getOrganization, (organization) => {
  return organization.zip || '';
});

export const getCountry = createSelector(getOrganization, (organization) => {
  return organization.country || '';
});

export const orgIsCanadian = createSelector(getCountry, (country) => country === 'CA');

export const getLat = createSelector(getOrganization, (organization) => {
  return organization.latitude || '';
});

export const getLong = createSelector(getOrganization, (organization) => {
  return organization.longitude || '';
});

export const getTimeZone = createSelector(getOrganization, (organization) => {
  return organization.time_zone || moment.tz.guess();
});

export const getOrgTimeZone = createSelector(getOrganization, (organization) => organization.time_zone);

export const getVideo = createSelector(getOrganization, (organization) => {
  return organization.video || {};
});

export const getLogoUrl = createSelector(getOrganization, (organization) => {
  return organization.logo_url;
});

export const getHasLogo = createSelector(getOrganization, (organization) => {
  return organization.has_logo;
});

export const getYoutubeId = createSelector(getVideo, (video) => {
  return video.youtube_id || '';
});

export const getDispatchablePros = createSelector(getOrganization, (organization) => {
  return organization.dispatchable_pros;
});

export const getSmsNotificationConfiguration = createSelector(getOrganization, (organization) => {
  if (isPresent(organization) && isPresent(organization.sms_notification_configuration)) {
    return organization.sms_notification_configuration.data;
  }
  return [];
});

export const isJobScheduledMessageNotificationEnabled = createSelector(
  getSmsNotificationConfiguration,
  (notificationConfig) => {
    if (isPresent(notificationConfig)) {
      let jobScheduledNotification = notificationConfig.find(
        (notification) => notification.type === NOTIFICATION_TYPE_JOB_SCHEDULED
      );
      return isPresent(jobScheduledNotification) ? jobScheduledNotification.enabled : true;
    }
    return true;
  }
);

export const getOrgInterests = createSelector(
  (state) => state.orgInterests,
  (interests) => {
    return interests || {};
  }
);
export const getInterests = createSelector(getOrgInterests, (interests) => {
  return interests.data;
});

export const orgTaxRates = createSelector(
  ({ organization = {} }) => organization,
  ({ organization_tax_rates: organizationTaxRates = {} }) => {
    const { data: orgTaxRates = [] } = organizationTaxRates;
    return Array.isArray(organizationTaxRates) ? organizationTaxRates : orgTaxRates;
  }
);

export const orgHasTaxRates = createSelector(orgTaxRates, (taxRates) => {
  const { length: taxRatesLength } = taxRates;
  return taxRatesLength > 0;
});

export const fetchOrganization = {
  fetchingApiFunc: OrganizationApi.fetchOrganization,
  selector: getOrganization,
  paramsFromProps: () => [],
  dataNamespace: ORGANIZATION_NAMESPACE,
};

export const fetchOrganizationInterests = {
  fetchingApiFunc: OrganizationApi.fetchOrganizationInterests,
  selector: getOrgInterests,
  paramsFromProps: () => [],
  dataNamespace: ORGANIZATION_INTERESTS_NAMESPACE,
};

export const fetchOrganizationProfile = {
  fetchingApiFunc: OrganizationApi.getOrganizationProfile,
  selector: getOrganization,
  paramsFromProps: () => [],
  dataNamespace: ORGANIZATION_NAMESPACE,
};

export const fetchOrganizationProfileWithExpansion = (expand) => ({
  fetchingApiFunc: OrganizationApi.getOrganizationProfile,
  selector: getOrganization,
  paramsFromProps: () => [expand],
  dataNamespace: ORGANIZATION_NAMESPACE,
  alwaysFetch: true,
});

export function receivedOrganizationData(data) {
  return (dispatch) => {
    dispatch(receivedServerData(data, ORGANIZATION_NAMESPACE));
  };
}
